/**
 * Risk Assessment Module Configuration
 *
 * Defines risk levels, likelihood/severity scales, ALARP zones,
 * control hierarchy, and other constants.
 *
 * @module config/moduleConfig
 */

// =============================================================================
// RISK LEVELS
// =============================================================================

/**
 * Risk level definitions with score ranges and colors
 */
export const RISK_LEVELS = {
  extreme: {
    label: 'Extreme',
    min: 20,
    max: 25,
    color: 'red',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-500',
    textColor: 'text-red-900',
    badgeColor: 'bg-red-600',
    description: 'Immediate action required. Work may not proceed.',
    action: 'Senior management approval mandatory. Implement controls immediately.'
  },
  high: {
    label: 'High',
    min: 12,
    max: 19,
    color: 'orange',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-500',
    textColor: 'text-orange-900',
    badgeColor: 'bg-orange-600',
    description: 'Senior management attention required.',
    action: 'Reduce to ALARP within set timeframe. Monitor closely.'
  },
  medium: {
    label: 'Medium',
    min: 6,
    max: 11,
    color: 'yellow',
    bgColor: 'bg-yellow-100',
    borderColor: 'border-yellow-500',
    textColor: 'text-yellow-900',
    badgeColor: 'bg-yellow-600',
    description: 'Management attention required.',
    action: 'Reduce to ALARP where practicable. Regular monitoring.'
  },
  low: {
    label: 'Low',
    min: 1,
    max: 5,
    color: 'green',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-500',
    textColor: 'text-green-900',
    badgeColor: 'bg-green-600',
    description: 'Acceptable with current controls.',
    action: 'Periodic review. Document and monitor.'
  }
};

/**
 * Get risk level from score
 * @param {number} score - Risk score (1-25)
 * @returns {string} Risk level key
 */
export const getRiskLevel = (score) => {
  if (score >= 20) return 'extreme';
  if (score >= 12) return 'high';
  if (score >= 6) return 'medium';
  return 'low';
};

// =============================================================================
// LIKELIHOOD SCALE (1-5)
// =============================================================================

/**
 * Likelihood scale definitions
 */
export const LIKELIHOOD_SCALE = {
  1: {
    value: 1,
    label: 'Rare',
    description: 'Once in 10+ years',
    frequency: 'May occur only in exceptional circumstances',
    color: 'green'
  },
  2: {
    value: 2,
    label: 'Unlikely',
    description: 'Once in 5-10 years',
    frequency: 'Could occur at some time',
    color: 'yellow'
  },
  3: {
    value: 3,
    label: 'Possible',
    description: 'Once in 1-5 years',
    frequency: 'Might occur at some time',
    color: 'yellow'
  },
  4: {
    value: 4,
    label: 'Likely',
    description: 'Once per year',
    frequency: 'Will probably occur in most circumstances',
    color: 'orange'
  },
  5: {
    value: 5,
    label: 'Almost Certain',
    description: 'Multiple times per year',
    frequency: 'Expected to occur in most circumstances',
    color: 'red'
  }
};

// =============================================================================
// SEVERITY SCALE (1-5)
// =============================================================================

/**
 * Severity scale definitions
 */
export const SEVERITY_SCALE = {
  1: {
    value: 1,
    label: 'Negligible',
    description: 'Minor injury, no time off',
    impact: 'No injuries, minor impact on operations',
    medicalTreatment: 'No treatment required or minor first aid',
    color: 'green'
  },
  2: {
    value: 2,
    label: 'Minor',
    description: 'First aid, 1-3 days off',
    impact: 'Minor injuries, slight operational impact',
    medicalTreatment: 'First aid treatment, 1-3 days off work',
    color: 'yellow'
  },
  3: {
    value: 3,
    label: 'Moderate',
    description: 'Medical treatment, 1-4 weeks off',
    impact: 'Medical treatment injury, moderate operational impact',
    medicalTreatment: 'Medical treatment, 1-4 weeks off work',
    color: 'yellow'
  },
  4: {
    value: 4,
    label: 'Major',
    description: 'Hospitalization, permanent disability',
    impact: 'Serious injury, significant operational impact',
    medicalTreatment: 'Hospitalization, permanent disability possible',
    color: 'orange'
  },
  5: {
    value: 5,
    label: 'Catastrophic',
    description: 'Fatality, multiple fatalities',
    impact: 'Death(s), severe operational/environmental impact',
    medicalTreatment: 'Single or multiple fatalities',
    color: 'red'
  }
};

// =============================================================================
// ALARP ZONES
// =============================================================================

/**
 * ALARP (As Low As Reasonably Practicable) zone definitions
 */
export const ALARP_ZONES = {
  intolerable: {
    label: 'Intolerable',
    min: 20,
    max: 25,
    color: 'red',
    bgColor: 'bg-red-100',
    description: 'Work must not proceed. Immediate action required.',
    requirement: 'Risk must be eliminated or reduced before work proceeds.',
    action: 'Senior management involvement mandatory. Implement controls immediately.'
  },
  alarp: {
    label: 'ALARP',
    min: 6,
    max: 19,
    color: 'yellow',
    bgColor: 'bg-yellow-100',
    description: 'Risk must be reduced to As Low As Reasonably Practicable.',
    requirement: 'Demonstrate that further risk reduction is not reasonably practicable.',
    action: 'Cost-benefit analysis for additional controls. Document decisions.'
  },
  acceptable: {
    label: 'Broadly Acceptable',
    min: 1,
    max: 5,
    color: 'green',
    bgColor: 'bg-green-100',
    description: 'Risk acceptable with current controls.',
    requirement: 'Maintain existing controls and monitor.',
    action: 'Periodic review sufficient. No immediate action required.'
  }
};

/**
 * Get ALARP zone from score
 * @param {number} score - Risk score (1-25)
 * @returns {string} ALARP zone key
 */
export const getAlarpZone = (score) => {
  if (score >= 20) return 'intolerable';
  if (score >= 6) return 'alarp';
  return 'acceptable';
};

// =============================================================================
// CONTROL HIERARCHY (ISO 45001)
// =============================================================================

/**
 * Hierarchy of controls (ISO 45001 standard)
 * Listed from most effective to least effective
 */
export const CONTROL_HIERARCHY = {
  elimination: {
    level: 1,
    label: 'Elimination',
    description: 'Remove the hazard entirely',
    examples: 'Avoid working at height, eliminate confined space entry, remove hazardous materials',
    effectiveness: 'Most Effective',
    color: 'green',
    icon: 'Ban'
  },
  substitution: {
    level: 2,
    label: 'Substitution',
    description: 'Replace with less hazardous alternative',
    examples: 'Use less hazardous chemicals, automate manual tasks, use battery tools instead of pneumatic',
    effectiveness: 'Very Effective',
    color: 'blue',
    icon: 'RefreshCw'
  },
  engineering: {
    level: 3,
    label: 'Engineering Controls',
    description: 'Physical changes to equipment or environment',
    examples: 'Machine guards, ventilation systems, noise barriers, fall protection systems',
    effectiveness: 'Effective',
    color: 'cyan',
    icon: 'Settings'
  },
  administrative: {
    level: 4,
    label: 'Administrative Controls',
    description: 'Procedures, training, and work practices',
    examples: 'Safe work procedures, permits to work, training, signage, job rotation',
    effectiveness: 'Less Effective',
    color: 'yellow',
    icon: 'FileText'
  },
  ppe: {
    level: 5,
    label: 'PPE (Personal Protective Equipment)',
    description: 'Personal protective equipment (last resort)',
    examples: 'Safety helmets, gloves, safety glasses, hearing protection, lifejackets',
    effectiveness: 'Least Effective',
    color: 'orange',
    icon: 'Shield'
  }
};

// =============================================================================
// REVIEW FREQUENCIES
// =============================================================================

/**
 * Risk assessment review frequency options
 */
export const REVIEW_FREQUENCIES = {
  afterIncident: {
    value: 'after_incident',
    label: 'After Incident',
    description: 'Review immediately following an incident or near miss',
    months: null
  },
  monthly: {
    value: 'monthly',
    label: 'Monthly',
    description: 'Review every month (for high-risk activities)',
    months: 1
  },
  quarterly: {
    value: 'quarterly',
    label: 'Quarterly',
    description: 'Review every 3 months',
    months: 3
  },
  biannually: {
    value: 'biannually',
    label: 'Bi-annually',
    description: 'Review every 6 months',
    months: 6
  },
  annually: {
    value: 'annually',
    label: 'Annually',
    description: 'Review every year (most common)',
    months: 12
  },
  biennial: {
    value: 'biennial',
    label: 'Biennial',
    description: 'Review every 2 years',
    months: 24
  },
  triennial: {
    value: 'triennial',
    label: 'Triennial',
    description: 'Review every 3 years (for low-risk activities)',
    months: 36
  }
};

// =============================================================================
// STATUS OPTIONS
// =============================================================================

/**
 * Risk assessment status workflow
 */
export const STATUS_OPTIONS = {
  draft: {
    value: 'draft',
    label: 'Draft',
    description: 'Assessment is being created',
    color: 'gray',
    icon: 'Edit',
    nextStates: ['under_review', 'approved']
  },
  underReview: {
    value: 'under_review',
    label: 'Under Review',
    description: 'Submitted for review (Comprehensive tier only)',
    color: 'blue',
    icon: 'Eye',
    nextStates: ['approved', 'draft']
  },
  approved: {
    value: 'approved',
    label: 'Approved',
    description: 'Assessment approved and ready for use',
    color: 'green',
    icon: 'CheckCircle',
    nextStates: ['active', 'archived']
  },
  active: {
    value: 'active',
    label: 'Active',
    description: 'Assessment in active use',
    color: 'green',
    icon: 'CheckCircle',
    nextStates: ['archived', 'under_review']
  },
  archived: {
    value: 'archived',
    label: 'Archived',
    description: 'Assessment no longer in use',
    color: 'gray',
    icon: 'Archive',
    nextStates: ['draft']
  }
};

// =============================================================================
// ASSESSMENT TYPES
// =============================================================================

/**
 * Types of risk assessments
 */
export const ASSESSMENT_TYPES = {
  generic: {
    value: 'generic',
    label: 'Generic Risk Assessment',
    description: 'For routine, regular activities',
    tier: 'both', // Available in Lite and Comprehensive
    icon: 'FileText',
    examples: 'Daily operations, routine maintenance, standard procedures'
  },
  taskSpecific: {
    value: 'task_specific',
    label: 'Task-Specific Risk Assessment',
    description: 'For specific non-routine tasks',
    tier: 'both',
    icon: 'Clipboard',
    examples: 'Cargo operations, hot work, lifting operations'
  },
  dynamic: {
    value: 'dynamic',
    label: 'Dynamic Risk Assessment',
    description: 'Real-time assessment for changing conditions',
    tier: 'comprehensive', // Comprehensive only
    icon: 'Activity',
    examples: 'Emergency response, unusual conditions, rapidly changing situations'
  },
  permitToWork: {
    value: 'permit_to_work',
    label: 'Permit-to-Work',
    description: 'For high-risk activities requiring formal authorization',
    tier: 'comprehensive', // Comprehensive only
    icon: 'FileCheck',
    examples: 'Confined space entry, working at height, hot work in hazardous areas'
  }
};

// =============================================================================
// MODULE SETTINGS
// =============================================================================

/**
 * General module configuration
 */
export const MODULE_SETTINGS = {
  // Risk matrix dimensions
  MATRIX_SIZE: 5, // 5x5 matrix

  // Default review frequency
  DEFAULT_REVIEW_FREQUENCY: 'annually',

  // Automatic risk level calculation
  AUTO_CALCULATE_RISK: true,

  // Show ALARP guidance
  SHOW_ALARP_GUIDANCE: true,

  // Require action plan for ALARP zone
  REQUIRE_ACTION_PLAN_ALARP: true,

  // Require action plan for Intolerable zone
  REQUIRE_ACTION_PLAN_INTOLERABLE: true,

  // PDF export settings
  PDF_INCLUDE_RISK_MATRIX: true,
  PDF_INCLUDE_CONTROL_HIERARCHY: true,
  PDF_INCLUDE_SIGNATURES: true,

  // Dashboard settings
  DASHBOARD_SHOW_OVERDUE: true,
  DASHBOARD_SHOW_HIGH_RISK: true,
  DASHBOARD_DAYS_UNTIL_DUE_WARNING: 30, // Show warning 30 days before due

  // Notification settings
  NOTIFY_OVERDUE_REVIEWS: true,
  NOTIFY_HIGH_RISK_ASSESSMENTS: true,
  NOTIFY_APPROVAL_REQUIRED: true,

  // Storage key for localStorage (vessel ID added automatically by useStorageState)
  STORAGE_KEY: 'risk_assessments',

  // Schema version for migration tracking
  SCHEMA_VERSION: 2
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate risk score from likelihood and severity
 * @param {number} likelihood - Likelihood value (1-5)
 * @param {number} severity - Severity value (1-5)
 * @returns {number} Risk score (1-25)
 */
export const calculateRiskScore = (likelihood, severity) => {
  return likelihood * severity;
};

/**
 * Get risk matrix cell color
 * @param {number} likelihood - Likelihood value (1-5)
 * @param {number} severity - Severity value (1-5)
 * @returns {string} Tailwind CSS color class
 */
export const getRiskMatrixColor = (likelihood, severity) => {
  const score = calculateRiskScore(likelihood, severity);
  const level = getRiskLevel(score);
  return RISK_LEVELS[level].bgColor;
};

/**
 * Validate risk assessment data
 * @param {Object} assessment - Risk assessment object
 * @returns {Object} Validation result { valid: boolean, errors: string[] }
 */
export const validateRiskAssessment = (assessment) => {
  const errors = [];

  if (!assessment.title || assessment.title.trim() === '') {
    errors.push('Title is required');
  }

  if (!assessment.type) {
    errors.push('Assessment type is required');
  }

  if (!assessment.hazards || assessment.hazards.length === 0) {
    errors.push('At least one hazard must be identified');
  }

  if (assessment.hazards) {
    assessment.hazards.forEach((hazard, index) => {
      if (!hazard.hazardDescription || hazard.hazardDescription.trim() === '') {
        errors.push(`Hazard ${index + 1}: Description is required`);
      }

      if (hazard.initialLikelihood < 1 || hazard.initialLikelihood > 5) {
        errors.push(`Hazard ${index + 1}: Invalid likelihood value`);
      }

      if (hazard.initialSeverity < 1 || hazard.initialSeverity > 5) {
        errors.push(`Hazard ${index + 1}: Invalid severity value`);
      }
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
};
